import React, { useState, useMemo } from 'react';
import { Code, X, Copy, Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

const EmbedCode = ({ entries, variant }) => {
  const { t } = useLanguage();
  const [showModal, setShowModal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [width, setWidth] = useState(600);
  const [height, setHeight] = useState(700);

  // Generate embed URL
  const embedUrl = useMemo(() => {
    if (entries.length === 0) return `${window.location.origin}${window.location.pathname}`;
    const enabledEntries = entries.filter(e => e.enabled !== false);
    const entriesParam = enabledEntries.map(e => encodeURIComponent(e.text)).join(',');
    return `${window.location.origin}${window.location.pathname}?entries=${entriesParam}&embed=true`;
  }, [entries]);

  // Generate iframe code
  const iframeCode = useMemo(() => {
    return `<iframe 
  src="${embedUrl}"
  width="${width}"
  height="${height}"
  frameborder="0"
  allow="fullscreen"
  title="wheel.expert - ${variant}"
></iframe>`;
  }, [embedUrl, width, height, variant]);

  // Copy to clipboard
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(iframeCode);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      const textArea = document.createElement('textarea');
      textArea.value = iframeCode;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return (
    <>
      <button
        onClick={() => setShowModal(true)}
        className="btn-action-bar"
        aria-label={t('wheel.embedCode')}
        title={t('wheel.embedCode')}
        data-testid="embed-button"
      >
        <Code size={20} />
        <span>{t('wheel.embedCode')}</span>
      </button>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal embed-modal" onClick={e => e.stopPropagation()}>
            <button className="modal-close" onClick={() => setShowModal(false)}>
              <X size={20} />
            </button>
            
            <h2>🖥️ {t('wheel.embedCode')}</h2>

            {/* Size controls */}
            <div className="embed-size-controls">
              <div className="size-input">
                <label>{t('wheel.embedWidth')} (px)</label>
                <input
                  type="number"
                  value={width}
                  onChange={(e) => setWidth(Math.max(300, parseInt(e.target.value) || 600))}
                  min={300}
                  max={1200}
                />
              </div>
              <div className="size-input">
                <label>{t('wheel.embedHeight')} (px)</label>
                <input
                  type="number"
                  value={height}
                  onChange={(e) => setHeight(Math.max(400, parseInt(e.target.value) || 700))}
                  min={400}
                  max={1000}
                />
              </div>
            </div>

            {/* Code preview */}
            <div className="embed-code-container">
              <pre className="embed-code">
                <code>{iframeCode}</code>
              </pre>
            </div>

            {/* Copy button */}
            <button onClick={handleCopy} className="btn-copy-embed">
              {copied ? <Check size={16} /> : <Copy size={16} />}
              {copied ? t('wheel.copied') : t('wheel.copyEmbedCode')}
            </button>

            {/* Preview */}
            <div className="embed-preview">
              <h3>{t('wheel.embedPreview')}</h3>
              <div 
                className="preview-frame-container" 
                style={{ 
                  width: '100%',
                  maxWidth: Math.min(width, 500),
                  height: Math.min(height * 0.5, 350),
                  margin: '0 auto',
                  border: '1px solid var(--color-border)',
                  borderRadius: 'var(--radius-md)',
                  overflow: 'hidden'
                }}
              >
                <iframe
                  src={embedUrl}
                  width="100%"
                  height="100%"
                  frameBorder="0"
                  title={t('wheel.embedPreview')}
                  style={{ border: 'none' }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default EmbedCode;
